import React from "react";
import { AlertTriangle } from "lucide-react";
import { ExtensionService } from "../services/extensionService";
import { formatDate } from "./formatDate";

export const getStatusStyle = (statusName) => {
    const s = (statusName || "").toLowerCase().trim();
    const styles = {
        "mới": { bg: "#FEF3C7", color: "#92400E" },
        "đã gửi": { bg: "#FEF3C7", color: "#92400E" },
        "đã tiếp nhận": { bg: "#DBEAFE", color: "#1E40AF" },
        "đang xử lý": { bg: "#DBEAFE", color: "#1E40AF" },
        "đã giải quyết": { bg: "#D1FAE5", color: "#065F46" },
        "đóng": { bg: "#E5E7EB", color: "#374151" },
        "từ chối": { bg: "#FEE2E2", color: "#991B1B" },
        "quá hạn": { bg: "#FEE2E2", color: "#B91C1C" },
        "xin gia hạn": { bg: "#FEF3C7", color: "#D97706" },
        "đã gia hạn": { bg: "#EDE9FE", color: "#6D28D9" },
    };
    return styles[s] || { bg: "#F3F4F6", color: "#6B7280" };
};


export const getUrgencyStyle = (mucDo) => {
    const styles = {
        "khẩn cấp": { bg: "#FEE2E2", color: "#991B1B", showIcon: true },
        "thông thường": { bg: "#F3F4F6", color: "#374151", showIcon: false },
        "cao": { bg: "#FEE2E2", color: "#991B1B", showIcon: true },
        "trung bình": { bg: "#FEF3C7", color: "#92400E", showIcon: false },
        "thấp": { bg: "#E5E7EB", color: "#6B7280", showIcon: false },
    };
    return styles[mucDo.toLowerCase()] || { bg: "#F3F4F6", color: "#6B7280", showIcon: false };
};

export const getCategoryStyle = (categoryName) => {
    const styles = {
        "môi trường": { bg: "black", color: "white" },
        "hạ tầng": { bg: "#DBEAFE", color: "#1E40AF" },
        "kiến nghị": { bg: "#D1FAE5", color: "#065F46" },
        "khác": { bg: "#E5E7EB", color: "#374151" },
    };
    return styles[categoryName.toLowerCase()] || { bg: "#E0E7FF", color: "#3730A3" };
};

export const renderStatusBadge = (lichSuTrangThai) => {
    if (!lichSuTrangThai || lichSuTrangThai.length === 0) {
        return (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium rounded-md bg-gray-100 text-gray-600 border border-gray-200">
                Chưa cập nhật
            </span>
        );
    }

    const currentStatus = lichSuTrangThai[0].ten;
    const style = getStatusStyle(currentStatus);

    return (
        <span
            className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-semibold rounded-md border"
            style={{
                backgroundColor: style.bg,
                color: style.color,
                borderColor: (style.color || '#374151') + '30',
            }}
        >
            {currentStatus}
        </span>
    );
};

export const renderCategoryBadge = (linhVuc) => {
    if (!linhVuc || !linhVuc.ten) {
        return <span className="text-xs text-gray-400">-</span>;
    }

    return (
        <span className="text-xs md:text-sm font-medium text-gray-900 truncate block" title={linhVuc.ten}>
            {linhVuc.ten}
        </span>
    );
};

export const renderCategoryBadgeStyled = (linhVuc) => {
    if (!linhVuc || !linhVuc.ten) {
        return (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium rounded-md bg-gray-100 text-gray-600 border border-gray-200">
                -
            </span>
        );
    }

    const style = getCategoryStyle(linhVuc.ten);

    return (
        <span
            className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-semibold rounded-md border"
            style={{
                backgroundColor: style.bg,
                color: style.color,
                borderColor: (style.color || '#3730A3') + '30',
            }}
        >
            {linhVuc.ten}
        </span>
    );
};

export const renderUrgencyBadge = (mucDo) => {
    if (!mucDo) {
        return (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium rounded-md bg-gray-100 text-gray-600 border border-gray-200">
                -
            </span>
        );
    }

    const style = getUrgencyStyle(mucDo);

    return (
        <span
            className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-semibold rounded-md border whitespace-nowrap"
            style={{
                backgroundColor: style.bg,
                color: style.color,
                borderColor: (style.color || '#991B1B') + '30',
            }}
        >
            {style.showIcon && <AlertTriangle className="w-3 h-3" />}
            {mucDo}
        </span>
    );
};

export const renderContactInfo = (contactInfo, maxWidth = 180) => {
    const name = contactInfo?.name || "Ẩn danh";
    const phone = contactInfo?.phone || null;
    const fullText = phone ? `${name}\n${phone}` : name;
    const isAnonymous = name === "Ẩn danh";

    return (
        <div className="text-sm overflow-wrap-anywhere max-w-full" style={{ maxWidth: `${maxWidth}px` }} title={fullText}>
            {isAnonymous ? (
                <span className="inline-flex items-center text-gray-600 bg-gray-100 px-2.5 py-1 rounded-md text-xs break-words">
                    {name}
                </span>
            ) : (
                <div className="space-y-1">
                    <div className="text-gray-900 break-words overflow-wrap-anywhere word-break-break-word">
                        {name}
                    </div>
                    {phone && (
                        <div className="text-gray-600 break-words overflow-wrap-anywhere word-break-break-word">
                            {phone}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export const getEffectiveDeadline = (report) => {
    if (!report) return null;

    const history = report.lich_su_trang_thai || [];
    const currentStatus = (
        history[0]?.ten || 
        history[history.length - 1]?.ten || 
        report.status || 
        ''
    ).toLowerCase();

    if (currentStatus.includes('đã gửi') || currentStatus.includes('mới') || currentStatus.includes('chờ duyệt')) {
        return null;
    }

    const directExtensions = report.de_nghi_gia_han_phan_anh || report.extensions || [];
    const serviceExtensions = ExtensionService?.getByComplaintId ? ExtensionService.getByComplaintId(report.id, report.ma_phan_anh) : [];
    const extensions = Array.isArray(directExtensions) && directExtensions.length > 0 ? directExtensions : serviceExtensions;

    const approvedExt = extensions.find((e) => (e.status === 'APPROVED' || e.trang_thai === 'APPROVED'));
    if (approvedExt?.approvedDeadline) return approvedExt.approvedDeadline;
    if (approvedExt?.han_de_xuat_moi) return approvedExt.han_de_xuat_moi;
    if (approvedExt?.han_moi) return approvedExt.han_moi;

    if (report.thong_tin_gia_han?.han_de_xuat_moi) return report.thong_tin_gia_han.han_de_xuat_moi;
    if (report.ngay_du_kien_hoan_thanh) return report.ngay_du_kien_hoan_thanh;
    if (report.thoi_gian_phan_hoi_du_kien) return report.thoi_gian_phan_hoi_du_kien;
    if (report.currentDeadline) return report.currentDeadline;

    return null;
};

export const getTimeRemaining = (deadlineOrReport) => {
    let deadline = deadlineOrReport;
    if (deadlineOrReport && typeof deadlineOrReport === 'object') {
        deadline = getEffectiveDeadline(deadlineOrReport);
    }
    if (!deadline) return null;
    const now = new Date();
    const dl = new Date(deadline);
    const diff = dl - now;
    const isOverdue = diff < 0;
    const abs = Math.abs(diff);
    const days = Math.floor(abs / 86400000);
    const hours = Math.floor((abs % 86400000) / 3600000);
    const mins = Math.floor((abs % 3600000) / 60000);
    if (isOverdue) {
        if (days > 0) return `Quá hạn ${days} ngày ${hours} giờ`;
        return `Quá hạn ${hours} giờ ${mins} phút`;
    }
    if (days > 0) return `Còn ${days} ngày ${hours} giờ`;
    return `Còn ${hours} giờ ${mins} phút`;
};

export const computeSlaStatus = (report) => {
    if (!report) return 'NOT_APPLICABLE';
    if (report.slaStatus) return report.slaStatus;

    const history = report.lich_su_trang_thai || [];
    const latestStatus = (history[0]?.ten || history[history.length - 1]?.ten || report.status || '').toLowerCase();

    if (latestStatus.includes('đã gửi') || latestStatus.includes('mới') || latestStatus.includes('chờ duyệt')) {
        return 'NOT_APPLICABLE';
    }

    if (latestStatus.includes('từ chối')) {
        return 'NOT_APPLICABLE';
    }

    const targetDeadline = getEffectiveDeadline(report);

    if (latestStatus.includes('giải quyết') || latestStatus.includes('đóng')) {
        if (!targetDeadline) {
            return 'COMPLETED_ON_TIME';
        }

        let completedAt = null;
        const resolvedEntries = history.filter((h) => {
            const t = (h?.ten || '').toLowerCase();
            return t.includes('giải quyết') || t.includes('đóng');
        });

        if (resolvedEntries.length > 0) {
            const timestamps = resolvedEntries
                .map((e) => (e.thoi_gian_tao ? new Date(e.thoi_gian_tao).getTime() : 0))
                .filter((ts) => !Number.isNaN(ts) && ts > 0);
            if (timestamps.length > 0) {
                completedAt = new Date(Math.max(...timestamps));
            }
        }

        if (!completedAt) {
            completedAt = report.thoi_gian_cap_nhat
                ? new Date(report.thoi_gian_cap_nhat)
                : (report.updatedAt ? new Date(report.updatedAt) : new Date());
        }

        const dl = new Date(targetDeadline);
        if (!Number.isNaN(dl.getTime()) && !Number.isNaN(completedAt.getTime())) {
            if (completedAt.getTime() > dl.getTime()) {
                return 'OVERDUE';
            }
        }

        return 'COMPLETED_ON_TIME';
    }

    const directExtensions = report.de_nghi_gia_han_phan_anh || report.extensions || [];
    const serviceExtensions = ExtensionService?.getByComplaintId ? ExtensionService.getByComplaintId(report.id, report.ma_phan_anh) : [];
    const extensions = Array.isArray(directExtensions) && directExtensions.length > 0 ? directExtensions : serviceExtensions;

    const pendingExt = extensions.find((e) => (e.status === 'PENDING' || e.trang_thai === 'PENDING'));
    if (pendingExt) return 'PENDING_EXTENSION';

    const approvedExt = extensions.find((e) => (e.status === 'APPROVED' || e.trang_thai === 'APPROVED'));
    const rejectedExt = extensions.find((e) => (e.status === 'REJECTED' || e.trang_thai === 'REJECTED'));

    if (!targetDeadline) return 'NOT_APPLICABLE';

    const now = new Date();
    const dl = new Date(targetDeadline);
    const diffHours = (dl - now) / 3600000;
    if (diffHours < 0) {
        return rejectedExt ? 'REJECTED_EXTENSION' : 'OVERDUE';
    }
    if (approvedExt) return 'EXTENDED';
    if (diffHours <= 24) return 'NEAR_DUE';

    return 'ON_TIME';
};

export const renderSlaBadge = (slaStatusOrReport) => {
    if (!slaStatusOrReport) return <span className="text-sm text-gray-400">-</span>;

    const report = typeof slaStatusOrReport === 'object' ? slaStatusOrReport : null;
    const status = typeof slaStatusOrReport === 'string' ? slaStatusOrReport : computeSlaStatus(report);

    const map = {
        'ON_TIME': { label: 'Còn hạn', bg: '#D1FAE5', color: '#065F46' },
        'NEAR_DUE': { label: 'Sắp đến hạn', bg: '#FEF3C7', color: '#92400E' },
        'OVERDUE': { label: 'Quá hạn', bg: '#FEE2E2', color: '#991B1B' },
        'REJECTED_EXTENSION': { label: 'Từ chối gia hạn', bg: '#FEE2E2', color: '#991B1B' },
        'COMPLETED_ON_TIME': { label: 'Đúng hạn', bg: '#D1FAE5', color: '#065F46' },
        'COMPLETED_LATE': { label: 'Trễ hạn', bg: '#FEE2E2', color: '#991B1B' },
        'PENDING_EXTENSION': { label: 'Chờ gia hạn', bg: '#FFEDD5', color: '#9A3412' },
        'EXTENDED': { label: 'Đã gia hạn', bg: '#DBEAFE', color: '#1E40AF' },
        'NOT_APPLICABLE': { label: 'Chưa áp dụng', bg: '#F3F4F6', color: '#6B7280' },
    };

    const s = map[status] || { label: status || 'Còn hạn', bg: '#D1FAE5', color: '#065F46' };
    const isPulse = status === 'OVERDUE' || status === 'REJECTED_EXTENSION';

    return (
        <span
            className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-semibold rounded-md border shadow-2xs whitespace-nowrap"
            style={{ backgroundColor: s.bg, color: s.color, borderColor: s.color + '40' }}
        >
            <span className={`w-1.5 h-1.5 rounded-full ${isPulse ? 'bg-red-600 animate-pulse' : ''}`} style={{ backgroundColor: s.color }} />
            {s.label}
        </span>
    );
};
